"use client"

import type React from "react"

import { useState, useRef, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Building2, Plus } from "lucide-react"
//import { useCompany, type Company } from "@/contexts/company-context"
import { useGetCompanies } from "openapi/api/endpoints"
import { Company } from "openapi/api/model"

interface CompanyAutocompleteProps {
  value: string
  onChange: (value: string, id?: string) => void
  onCreateCompany?: (companyName: string) => void
  placeholder?: string
  className?: string
}

const searchCompanies = (query: string, companies: Company[]): Company[] => {
    if (!query.trim()) return companies
    const lowercaseQuery = query.toLowerCase()
    return companies.filter(
      (company) =>
        company.name.toLowerCase().includes(lowercaseQuery) ||
        company.industry?.toLowerCase().includes(lowercaseQuery) ||
        company.location?.toLowerCase().includes(lowercaseQuery),
    )
  }

export function CompanyAutocomplete({
  value,
  onChange,
  onCreateCompany,
  placeholder,
  className,
}: CompanyAutocompleteProps) {
  //const { companies, searchCompanies } = useCompany()
  const [isOpen, setIsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<Company[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const { data } = useGetCompanies()


  useEffect(() => {
    if (value.trim()) {
      const matches = searchCompanies(value, data?.items??[])
      setSuggestions(matches.slice(0, 5)) // Show top 5 matches
      setIsOpen(matches.length > 0 || value.length > 2)
    } else {
      setSuggestions([])
      setIsOpen(false)
    }
  }, [value, searchCompanies])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value, undefined)
  }

  const handleSuggestionClick = (company: Company) => {
    onChange(company.name, company.id)
    setIsOpen(false)
  }

  const handleCreateNew = () => {
    if (onCreateCompany) {
      onCreateCompany(value)
    }
    setIsOpen(false)
  }

  const showCreateOption =
    value.trim().length > 2 && !data?.items?.some((c) => c.name.toLowerCase() === value.toLowerCase())

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={className}
        onFocus={() => {
          if (suggestions.length > 0 || showCreateOption) {
            setIsOpen(true)
          }
        }}
        onBlur={() => {
          // Delay closing to allow clicking on suggestions
          setTimeout(() => setIsOpen(false), 200)
        }}
      />

      {isOpen && (suggestions.length > 0 || showCreateOption) && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((company) => (
            <div
              key={company.id}
              className="flex items-center space-x-2 px-3 py-2 hover:bg-accent cursor-pointer"
              onClick={() => handleSuggestionClick(company)}
            >
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <p className="font-medium">{company.name}</p>
                {company.industry && <p className="text-xs text-muted-foreground">{company.industry}</p>}
              </div>
            </div>
          ))}

          {showCreateOption && (
            <div
              className="flex items-center space-x-2 px-3 py-2 hover:bg-accent cursor-pointer border-t"
              onClick={handleCreateNew}
            >
              <Plus className="h-4 w-4 text-brand-ink" />
              <div className="flex-1">
                <p className="font-medium text-brand-ink">Create "{value}"</p>
                <p className="text-xs text-muted-foreground">Add as new company</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}